/* prettier-ignore */

import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { LOCATION_ICON, MAP_STYLE, PRIMARY_TIMELINE_COLORS, SECONDARY_TIMELINE_COLOR } from '../../constants';

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
    private map?: google.maps.Map;
    private itemsRouteCoordinates: any[] = [];
    private animatePolylineInterval: any;

    private static mapLoader: Promise<typeof google> | null = null;
    private markers: google.maps.Marker[] = [];
    private polylines: google.maps.Polyline[] = [];
    private circles: google.maps.Circle[] = [];
    private bounds: google.maps.LatLngBounds;
    public overlayView: google.maps.OverlayView;
    private panes: google.maps.MapPanes;
    private zoomListener: any;

// --------------------------------------- MAP  --------------------------------------- //

    async initializeMap(element: HTMLElement, options: google.maps.MapOptions): Promise<google.maps.Map> {
        if (!GoogleMapsService.mapLoader) {
            GoogleMapsService.mapLoader = new Loader({
                apiKey: 'AIzaSyCosmIo9py_6Dfd_mr1CWSJxQtDzl1LWjA',
                version: 'weekly',
                libraries: ['geometry', 'places'],
            }).load();
        }
        await GoogleMapsService.mapLoader;
    
        this.map = new google.maps.Map(element, options);
        return this.map;
    }
    
    public clearMap(): void {
        this.removeZoomListener();
        this.clearOverlayView();
        this.resetBounds();
        this.clearMarkers();
        this.clearPolylines();
        this.clearCircles();
    }

    public addZoomListener(){
        this.removeZoomListener();
        this.zoomListener = this.map.addListener("zoom_changed", () => {
            const size = this.map.getZoom();
        });
    }

    public removeZoomListener(){
        if(!this.zoomListener) return;
        google.maps.event.removeListener(this.zoomListener);
    }

    public setLightMapStyle(): void {
        const styledMap = new google.maps.StyledMapType(MAP_STYLE['light'], { name: 'Light Map' });
        this.map.mapTypes.set('map_light', styledMap);
        this.map.setMapTypeId('map_light');
    }

    public setDarkMapStyle(): void {
        const styledMap = new google.maps.StyledMapType(MAP_STYLE['dark'], { name: 'Dark Map' });
        this.map.mapTypes.set('map_dark', styledMap);
        this.map.setMapTypeId('map_dark');
    }

    public fitBounds(): void {
        if (!this.bounds) return;
        this.map?.fitBounds(this.bounds);
    }

    public extendBounds(location): void {
        this.bounds?.extend(location);
    }

    public resetBounds(): void {
        this.bounds = new google.maps.LatLngBounds();
    }

    public resetItemsRouteCoordinates(): void{
        this.itemsRouteCoordinates = [];
    }

// --------------------------------------- ROUTE MARKERS  --------------------------------------- //
    public createMarker(location: any, label: string | number, color: string, isAddPoint = false): google.maps.Marker {
        const icon = {
            url: `data:image/svg+xml;base64,${btoa(LOCATION_ICON(color, label))}`,
            scaledSize: new google.maps.Size(40, 40),
        };

        let markerLabel = null;
        // if (isAddPoint){
        //     if (label === 'I' || label === 'F'){
        //         markerLabel = {
        //             className: 'marker-label',
        //             text: label === 'I' ? 'Inicio' : 'Final', 
        //             color: '#FFF', 
        //             fontSize: '16px', 
        //             fontWeight: 'bold',
        //         }
        //     }
        // }

        let animation = null;
        if (isAddPoint && label === "F")
            animation = google.maps.Animation.BOUNCE;

        const marker = new google.maps.Marker({
            map: this.map,
            position: new google.maps.LatLng(location.lat, location.lng),
            label: markerLabel,
            icon,
            animation
        });

        this.addMarker(marker);
        this.extendBounds(marker.getPosition())
        this.itemsRouteCoordinates = [
            ...this.itemsRouteCoordinates,
            [location.lng, location.lat]
        ];
        return marker;
    }

    public addMarker(marker: google.maps.Marker): void {
        this.markers = [...this.markers, marker];
    }

    public clearMarkers(): void {
        this.markers?.forEach(marker => marker.setMap(null));
    }

// --------------------------------------- ROUTE POLYLINES  --------------------------------------- //
    getRoute(day: number): void {
        const itemsCoordinates = this.itemsRouteCoordinates.join(';');
        let request = new XMLHttpRequest();
        request.open('GET', "https://cms-iurban.com/osrm_foot/route/v1/driving/"+ itemsCoordinates +"?steps=true&overview=full");
        request.setRequestHeader('Accept', 'application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8');
        request.setRequestHeader('Content-Type', 'application/json');
        request.onreadystatechange = ((request: any) => {
            if (request.readyState === 4) {
                const response = JSON.parse(request.responseText);
                this.drawPolylineOnMap(response, day);
            }
        }).bind(undefined, request);
        if (this.itemsRouteCoordinates.length > 1)
            request.send();
    }

    drawPolylineOnMap(response: any, day: number): void {
        const primaryColor = PRIMARY_TIMELINE_COLORS[day];
        const secondaryColor = SECONDARY_TIMELINE_COLOR[day];
        const geometry = response.routes[0].geometry;
        const geometryDecoded = this.polylineDecode(geometry , 5);
        const polyline = new google.maps.Polyline({
            path: geometryDecoded,
            strokeColor: secondaryColor,
            strokeWeight: 5,
            map: this.map
        });
        this.polylines = [...this.polylines, polyline];
        this.animatePolyline(geometryDecoded, primaryColor)
    }

    animatePolyline(polylinesDecoded: any, color: string): void {
        let index = 0;
        if (this.animatePolylineInterval)
            clearInterval(this.animatePolylineInterval);

        this.animatePolylineInterval = setInterval(() => {
            if (index >= polylinesDecoded.length - 1) {
                clearInterval(this.animatePolylineInterval);
                return;
            }
            const segment = [polylinesDecoded[index], polylinesDecoded[index + 1]];
            const polyline = new google.maps.Polyline({
                path: segment,
                strokeColor: color,
                strokeWeight: 5,
                zIndex: 1,
                map: this.map,
            });
            this.polylines = [...this.polylines, polyline];
            index++;
        }, 15);
    }

    public drawPolylineBetweenStartEnd(path: google.maps.LatLngLiteral[]): void {
        const polyline = new google.maps.Polyline({
            path,
            geodesic: true,
            strokeOpacity: 0,
            icons: [{ icon: this.getLineSymbol(), offset: '0', repeat: '20px' }],
            strokeWeight: 5,
            map: this.map,
        });
        this.polylines = [...this.polylines, polyline];
        this.test(polyline)
    }

    private test(polyline: google.maps.Polyline, speedFactor = 0.1): void {
        let count = 0;
    
        const animate = () => {
            count = (count + speedFactor) % 200; // Incrementa el offset lentamente
            const icons = polyline.get('icons');
            if (icons && icons.length > 0) {
                icons[0].offset = `${count / 2}%`; // Actualiza el offset del ícono
                polyline.set('icons', icons); // Aplica los cambios a la polilínea
            }
            requestAnimationFrame(animate); // Llama a la animación en el próximo frame
        };
    
        animate(); // Inicia la animación
    }

    public drawPolylineBorderOnCircle(distance, radiusMeters: number){
        const polyline = new google.maps.Polyline({
            path: this.getCircle(distance, radiusMeters / 1609.344, 1),
            strokeOpacity: 0,
            icons: [{ icon: this.getLineSymbol(), offset: "0", repeat: "20px" }],
            strokeWeight: 2,
            strokeColor: "#fff",
            map: this.map,
        });
        this.polylines = [...this.polylines, polyline];
        this.test(polyline, 0.2)

    }

    private clearPolylines(): void {
        if (this.animatePolylineInterval) clearInterval(this.animatePolylineInterval);
        this.polylines.forEach(polyline => polyline.setMap(null));
    }
     
// --------------------------------------- CIRCLES  --------------------------------------- //
    public drawCircle(center: google.maps.LatLngLiteral, radiusMeters: number): void {
        const circle = new google.maps.Circle({
            strokeColor: '#FFf',
            strokeOpacity: 0,
            strokeWeight: 0,
            fillColor: '#13505B',
            fillOpacity: 0.35,
            map: this.map,
            center,
            radius: radiusMeters,
        });
        this.circles = [...this.circles, circle];
    }

    clearCircles(): void {
        this.circles?.forEach(circle => circle.setMap(null));
    }

// --------------------------------------- ON ADD ITEM OVERLAY   --------------------------------------- //
    public setOverlayView(adjustedPoint, distanceLabelMap){
        this.overlayView = new google.maps.OverlayView();
        this.overlayView.draw = () => {
          const projection = this.overlayView.getProjection();
          const divPosition = projection.fromLatLngToDivPixel(new google.maps.LatLng(adjustedPoint));
          if (!divPosition) return;
          this.panes = this.overlayView.getPanes();
          this.panes.overlayLayer.appendChild(distanceLabelMap);
          distanceLabelMap.style.left = `${divPosition.x - 40}px`;
          distanceLabelMap.style.top = `${divPosition.y - 40}px`;
        };
        this.overlayView.onRemove = () => this.panes?.overlayLayer?.removeChild(distanceLabelMap);
        this.overlayView.setMap(this.map);
    }

    clearOverlayView(){
        this.setLightMapStyle();
        this.overlayView?.setMap(null);
    }

// --------------------------------------- AUX FUNCTIONS  --------------------------------------- //
    private polylineDecode(str, precision): any {
        let index = 0, lat = 0, lng = 0, coordinates = [], shift = 0, result = 0, byte = null, latitude_change, longitude_change, factor = Math.pow(10, precision || 6);
        while (index < str.length) {
            byte = null;
            shift = 0;
            result = 0;
            do {
                byte = str.charCodeAt(index++) - 63;
                result |= (byte & 0x1f) << shift;
                shift += 5;
            } while (byte >= 0x20);
            latitude_change = result & 1 ? ~(result >> 1) : result >> 1;
            shift = result = 0;
            do {
                byte = str.charCodeAt(index++) - 63;
                result |= (byte & 0x1f) << shift;
                shift += 5;
            } while (byte >= 0x20);
            longitude_change = result & 1 ? ~(result >> 1) : result >> 1;
            lat += latitude_change;
            lng += longitude_change;
            coordinates.push({ lat: lat / factor, lng: lng / factor });
        }
        return coordinates;
    }

    private getCircle(point, radius, dir): google.maps.LatLng[] {
        let ey, ex;
        const d2r = Math.PI / 180;
        const r2d = 180 / Math.PI;
        const earthsradius = 3963;
        const points = 32;
        const rlat = (radius / earthsradius) * r2d;
        const rlng = rlat / Math.cos(point.lat * d2r);
        const extp = new Array();
        const start = dir === 1 ? 0 : points + 1;
        const end = dir === 1 ? points + 1 : 0
        for (let i = start; dir === 1 ? i < end : i > end; i = i + dir) {
            const theta = Math.PI * (i / (points / 2));
            ey = point.lng + rlng * Math.cos(theta);
            ex = point.lat + rlat * Math.sin(theta);
            extp.push(new google.maps.LatLng(ex, ey));
        }
        return extp;
    }

    private getLineSymbol(): google.maps.Symbol {
        return {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            strokeColor: '#fff',
            scale: 4,
        };
    }

}